import React from "react";
import { useApp } from "../../context/AppContext";

export const DEPTS = {
  engineering: "工程部", design: "设计部", sales: "销售部", marketing: "市场部",
  finance: "财务部", warehouse: "仓储部", admin_it: "行政/IT",
};
export const DEPTS_EN = {
  engineering: "Construction Dept", design: "Design", sales: "Sales", marketing: "Marketing",
  finance: "Finance", warehouse: "Warehouse", admin_it: "Admin/IT",
};
export const EMP_STATUS = {
  probation: { label: "试用期", en: "Probation", cls: "bg-amber-100 text-amber-700" },
  regular: { label: "正式", en: "Regular", cls: "bg-emerald-100 text-emerald-700" },
  left: { label: "已离职", en: "Departed", cls: "bg-slate-200 text-slate-500" },
};
// 2026-10-14 变更（对应 HR 反馈「假期类型不全」）：在原有类型基础上补齐美国口径的合规假期类型，
// 与后端 hrms_module.py 的 LEAVE_TYPES 保持一致（pto/psl/lwop/jury/bereavement/fmla）。
export const LEAVE_TYPES = {
  annual: "年假", sick: "病假", personal: "事假", comp: "调休", trip: "出差",
  pto: "PTO 带薪年假", psl: "PSL 带薪病假", lwop: "LWOP 授权无薪假",
  jury: "Jury Duty 陪审团义务", bereavement: "Bereavement 丧假", fmla: "FMLA/CFRA 家庭医疗假",
  pdl: "PDL 加州带薪家事假",
};
export const LEAVE_TYPES_EN = {
  annual: "Annual", sick: "Sick", personal: "Personal", comp: "Comp time", trip: "Business trip",
  pto: "PTO", psl: "PSL (Paid Sick Leave)", lwop: "LWOP", jury: "Jury Duty",
  bereavement: "Bereavement", fmla: "FMLA/CFRA", pdl: "PDL (CA Paid Family Leave)",
};
// 福利计划类别（医保 / 牙科 / 视力 / 退休），与后端 BENEFIT_TYPES 对齐。
export const BENEFIT_TYPES = { medical: "医疗保险", dental: "牙科保险", vision: "视力保险", retirement: "退休计划" };
export const BENEFIT_TYPES_EN = { medical: "Medical", dental: "Dental", vision: "Vision", retirement: "Retirement" };
// 用工/雇佣类型（2026-10-14 新增：补齐 1099 与 intern 实习生身份，对应 HR 反馈「缺 1099 / intern 身份类型」）。
export const EMPLOY_TYPES = {
  fulltime: { label: "全职(W-2)", en: "Full-time (W-2)" },
  parttime: { label: "兼职", en: "Part-time" },
  contractor: { label: "合同工", en: "Contractor" },
  "1099": { label: "1099 独立承包商", en: "1099 Contractor" },
  intern: { label: "实习生", en: "Intern" },
};
// 员工文档类别（2026-10-14 新增：员工档案文档区）。
export const EMP_DOC_KINDS = {
  performance: "绩效文档", personal: "个人资料", training: "培训文档",
  i9: "I-9 工作资格", w2: "W-2 税务表", contract: "劳动合同", other: "其他",
};
export const EMP_DOC_KINDS_EN = {
  performance: "Performance", personal: "Personal docs", training: "Training",
  i9: "I-9 Employment", w2: "W-2 Tax form", contract: "Contract", other: "Other",
};
export const CRED_TYPES = { license: "驾驶执照", professional: "专业证书", visa: "签证/工作许可", insurance: "保险", safety: "安全证书(OSHA等)", other: "其他" };
export const CRED_TYPES_EN = { license: "Driver's License", professional: "Professional Cert", visa: "Visa/Work Permit", insurance: "Insurance", safety: "Safety Cert (OSHA)", other: "Other" };
export const TR_TYPES = { onboard: "入职", offboard: "离职", transfer: "调岗" };
export const TR_TYPES_EN = { onboard: "Onboarding", offboard: "Offboarding", transfer: "Transfer" };
export const TASK_OWNERS = { hr: "HR", manager: "直属主管", it: "IT", finance: "财务" };
export const TASK_OWNERS_EN = { hr: "HR", manager: "Manager", it: "IT", finance: "Finance" };
export const MANAGE_ROLES = ["admin", "ceo", "hr"];

/**
 * 【2026-10-26 权限体系改造第四批】HR 管理动作判定：优先读权限快照，未加载时回落原名单。
 *
 * 为什么需要这个 hook：HR 有 12 个页面各自写 `MANAGE_ROLES.includes(user.role)`，
 * 全部是同一份硬编码名单。管理员在「角色权限矩阵」里给某岗位勾上 HR 页面后，
 * 页面进得去但所有管理按钮仍然隐藏 —— 因为前端读的是角色、后端读的是角色，
 * 两处都不看授权。改为统一读 permOf("/hrms/employees::manage")（= 后端同一份判定），
 * 既让授权真正生效，也保证「前端按钮可见性」与「后端 403 判定」永远同源。
 *
 * 为什么用 `??` 而不是 `||`：permOf 在快照未加载 / 该键无条目时返回 undefined，
 * 此时必须回落到旧名单（首屏与加载失败时行为与改造前一致）；
 * 而 false 是「明确无权」，不能被 || 吞掉转去用旧名单。
 */
export function useHrmsManage(user) {
  const { permOf } = useApp();
  return permOf("/hrms/employees::manage") ?? MANAGE_ROLES.includes(user?.role);
}

/**
 * 【2026-10-26 第四批】薪资页专用判定：权限点开门 + 「区域」作为数据范围再收一道。
 *
 * 为什么单独两个 hook 而不复用 useHrmsManage：中国 / 美国薪资的准入历来是
 * 「角色名单 **且** 区域」两段。角色名单现在可配（读动作点 /hrms/cn-payroll::manage、
 * /hrms/us-payroll::manage，目录默认角色 = hr/ceo/admin/finance，与后端模块 MANAGE 常量一致）；
 * 而区域（cn / 非 cn）属于**数据范围**，不进权限配置 —— 否则管理员给一个加州财务勾上
 * 「中国薪酬社保」，他会以为能维护中国薪酬，实际仍被区域拦下，比不改还难解释。
 *
 * 为什么分「未加载」与「有判定」两条路：
 *   · permOf 返回 undefined = 快照未加载 / 该键无条目 → 回落改造前的原表达式（逐字一致，
 *     首屏与加载失败时不会出现「本来能看突然不能看」）；
 *   · 有判定（含显式屏蔽 false）→ 以权限点为准，再叠区域判定。
 *     区域口径与后端逐条对齐（见 cn_payroll._manage / us_payroll._scope_branch）：
 *     · 中国薪酬：除 admin / ceo 外一律要求 region=cn（后端 2026-10-26 已补此缺口，
 *       否则一个 la 的 member 获授后能读中国工资表）；
 *     · 美国薪资：hr / finance 且 region=cn 被拒。
 *     两侧同源，不会出现「页面放行但接口 403」或反之。
 */
export function useCnPayrollAccess(user) {
  const { permOf } = useApp();
  const p = permOf("/hrms/cn-payroll::manage");
  const isTop = ["admin", "ceo"].includes(user?.role);
  if (p === undefined) return MANAGE_ROLES.includes(user?.role) || (user?.role === "finance" && user?.region === "cn");
  const regionOK = isTop ? true : (user?.region || "") === "cn";
  return p && regionOK;
}
export function useUsPayrollAccess(user) {
  const { permOf } = useApp();
  const p = permOf("/hrms/us-payroll::manage");
  if (p === undefined) {
    return ["admin", "ceo"].includes(user?.role) || (["hr", "finance"].includes(user?.role) && user?.region !== "cn");
  }
  const regionOK = ["hr", "finance"].includes(user?.role) ? user?.region !== "cn" : true;
  return p && regionOK;
}
// 2026-06 用户要求：HR 只能看到所属分公司（admin / CEO 全公司）
export const hrScope = (user) => (user?.role === "hr" ? (user.region || "__none__") : "");
export const CUR_SYM = { CNY: "¥", USD: "$" };
export const fmtMoney = (n, cur = "USD") => (CUR_SYM[cur] || "$") + Math.round(n || 0).toLocaleString();
export const ScopeBadge = ({ scope, branches = [], zh = true }) => {
  if (!scope) return null;
  const b = branches.find((x) => x.id === scope);
  const name = b ? (zh ? b.zh : (b.en || b.zh)) : scope;
  return <span data-testid="hrms-scope-badge" className="inline-flex items-center text-[11px] px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">{zh ? `仅显示所属分公司：${name}` : `Scoped to your entity: ${name}`}</span>;
};
// 2026-06 用户要求：除 HR / CEO 外（含经理 / VP）仅显示本人资料 → 部门经理不再拥有 HR 查看权限
export const DEPT_MGR_ROLES = [];
// HR 表单在手机上改单列时，日期和长选项也必须允许收窄，避免原生控件固有宽度撑破弹窗。
export const inp = "mt-1 w-full min-w-0 max-w-full text-sm rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400";
export const Badge = ({ conf, zh = true }) => conf ? <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${conf.cls}`}>{zh ? conf.label : (conf.en || conf.label)}</span> : null;
export const credBadge = (days, zh = true) => {
  if (days == null) return null;
  if (days < 0) return <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-600">{zh ? `已过期 ${-days} 天` : `Expired ${-days}d`}</span>;
  if (days <= 7) return <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-600">{zh ? `剩 ${days} 天` : `${days}d left`}</span>;
  if (days <= 30) return <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">{zh ? `剩 ${days} 天` : `${days}d left`}</span>;
  if (days <= 60) return <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-700">{zh ? `剩 ${days} 天` : `${days}d left`}</span>;
  return null;
};
export const daysTo = (d) => {
  if (!d) return null;
  return Math.round((new Date(d + "T00:00:00") - new Date(new Date().toDateString())) / 86400000);
};
export const getGeo = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
    () => resolve(null), { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 });
});
